/**
 * Export Service
 *
 * Handles exporting agents, teams, and workflows to various formats
 */
import type { Agent, AgentTeam, Workflow } from '@/types'

export interface ExportOptions {
  format?: 'json' | 'yaml'
  pretty?: boolean
  includeMetadata?: boolean
}

export interface ExportMetadata {
  exportedAt: string
  exportedBy?: string
  version: string
  platform: string
  itemCount: number
}

export interface ExportData<T> {
  metadata: ExportMetadata
  data: Partial<T>[]
}

class ExportService {
  /**
   * Export agents to JSON
   */
  exportAgents(agents: Agent[], options: ExportOptions = {}): string {
    const { pretty = true, includeMetadata = true } = options

    const exportData: ExportData<Agent> = {
      metadata: {
        exportedAt: new Date().toISOString(),
        version: '1.0.0',
        platform: 'AI Agent Platform',
        itemCount: agents.length,
      },
      data: agents.map(this.sanitizeAgent),
    }

    if (!includeMetadata) {
      return JSON.stringify(agents.map(this.sanitizeAgent), null, pretty ? 2 : 0)
    }

    return JSON.stringify(exportData, null, pretty ? 2 : 0)
  }

  /**
   * Export teams to JSON
   */
  exportTeams(teams: AgentTeam[], options: ExportOptions = {}): string {
    const { pretty = true, includeMetadata = true } = options

    const exportData: ExportData<AgentTeam> = {
      metadata: {
        exportedAt: new Date().toISOString(),
        version: '1.0.0',
        platform: 'AI Agent Platform',
        itemCount: teams.length,
      },
      data: teams.map(this.sanitizeTeam),
    }

    if (!includeMetadata) {
      return JSON.stringify(teams.map(this.sanitizeTeam), null, pretty ? 2 : 0)
    }

    return JSON.stringify(exportData, null, pretty ? 2 : 0)
  }

  /**
   * Export workflows to JSON
   */
  exportWorkflows(workflows: Workflow[], options: ExportOptions = {}): string {
    const { pretty = true, includeMetadata = true } = options

    const exportData: ExportData<Workflow> = {
      metadata: {
        exportedAt: new Date().toISOString(),
        version: '1.0.0',
        platform: 'AI Agent Platform',
        itemCount: workflows.length,
      },
      data: workflows.map(this.sanitizeWorkflow),
    }

    if (!includeMetadata) {
      return JSON.stringify(workflows.map(this.sanitizeWorkflow), null, pretty ? 2 : 0)
    }

    return JSON.stringify(exportData, null, pretty ? 2 : 0)
  }

  /**
   * Download JSON file
   */
  downloadJSON(data: string, filename: string): void {
    const blob = new Blob([data], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  /**
   * Generate filename with timestamp
   */
  generateFilename(type: string, count: number): string {
    const timestamp = new Date().toISOString().split('T')[0]
    const itemText = count === 1 ? type : `${type}s`
    return `${itemText}_${count}_${timestamp}.json`
  }

  /**
   * Sanitize agent data for export (remove sensitive/internal fields)
   */
  private sanitizeAgent(agent: Agent): Partial<Agent> {
    const {
      id,
      workspace_id,
      created_at,
      updated_at,
      created_by,
      ...exportData
    } = agent

    return exportData
  }

  /**
   * Sanitize team data for export
   */
  private sanitizeTeam(team: AgentTeam): Partial<AgentTeam> {
    const {
      id,
      workspace_id,
      created_at,
      updated_at,
      created_by,
      ...exportData
    } = team

    return exportData
  }

  /**
   * Sanitize workflow data for export
   */
  private sanitizeWorkflow(workflow: Workflow): Partial<Workflow> {
    const {
      id,
      workspace_id,
      created_at,
      updated_at,
      created_by,
      ...exportData
    } = workflow

    return exportData
  }

  /**
   * Export configuration backup (multiple types)
   */
  exportBackup(data: {
    agents?: Agent[]
    teams?: AgentTeam[]
    workflows?: Workflow[]
  }): string {
    const backup = {
      metadata: {
        exportedAt: new Date().toISOString(),
        version: '1.0.0',
        platform: 'AI Agent Platform',
        type: 'full_backup',
      },
      agents: data.agents?.map(this.sanitizeAgent) || [],
      teams: data.teams?.map(this.sanitizeTeam) || [],
      workflows: data.workflows?.map(this.sanitizeWorkflow) || [],
    }

    return JSON.stringify(backup, null, 2)
  }
}

export const exportService = new ExportService()
export default exportService
